import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import Checkbox from '@material-ui/core/Checkbox';
import styled from 'styled-components';
import { colors } from '../../constants/colors';
import { analyticsTrackEvent } from '../../core/analytics';
import FormHelperTextError from '../SolvDesignSystem/Form/FormHelperTextError';

const StyledLabel = styled(FormControlLabel).withConfig({ componentId: 'sc-zjbe35-0' })`
  & .label {
    color: ${colors.lanaDelGray};
  }
`;

type Size = 'medium' | 'small';

type Props = {
  /** The Field object with relevant input props */
  field: any;

  /** The id of the field */
  id: string;

  /** The size of the checkbox */
  size?: Size;

  /** Name of the event to track */
  trackEvent?: string;

  /** Whether the form has been submitted */
  submitted?: boolean;

  /** A label for the checkbox */
  label: ReactNode;

  /**
   * TECH DEBT: This isn't used. But existing implementations cause side effects when they set
   * it which correctly setup validation for these checkboxes.
   */
  inputProps: any;
};

/**
 * A checkbox input component which matches the API expected by the form
 * helpers used in the Booking Widget, but is just a Material Checkbox under
 * the hood.
 */
const CheckboxInput = ({ field, id, label, trackEvent, submitted, size = 'small' }: Props) => {
  const [touched, setTouched] = useState(false);

  const handleOnFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      field.onFocus(e);
      if (trackEvent) {
        analyticsTrackEvent(trackEvent);
      }
    },
    [field, trackEvent]
  );

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e && e.target) {
        // TODO check in about triggering onfocus event in onchange
        handleOnFocus(e as React.FocusEvent<HTMLInputElement>);
        setTouched(true);
        field.onChange(e.target.checked);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setTouched, field]
  );

  const error = useMemo(() => {
    return field?.error?.[0];
  }, [field]);

  const shouldShowError = useMemo(
    () => error && (submitted || touched),
    [error, submitted, touched]
  );

  return (
    <FormControl error={shouldShowError}>
      <StyledLabel
        classes={{
          label: 'label',
        }}
        control={
          <Checkbox checked={field.value} id={id} name={id} onChange={handleOnChange} size={size} />
        }
        label={label}
      />
      {shouldShowError && <FormHelperTextError errorText={error} />}
    </FormControl>
  );
};

export default CheckboxInput;
